<template>
  <div class="wrapper" @click="emit('close')">
    <div class="facet-list" @click.stop>
      <OverlayScrollbars :options="scrollbarOptions">
        <ArrowLeftIcon @click="emit('close')" 
                        class="close-button"/>
        <h3>{{ props.fieldName }}</h3>
        <div v-if="loading">Loading...</div>
        <table v-else>
          <tr aria-label="Sortable header row">
            <th></th>
            <th @click="doSort('count')">
              Count
              <TriangleFilledIcon
                v-show="sortKey === 'count'"
                size="14"
                :style="{ position: 'relative', top: '0.2rem', left: '0.2rem', transform: sortDir === 'ascending' ? 'rotate(180deg)' : 'rotate(0deg)' }"  />
            </th>
            <th @click="doSort('value')">Value
              <TriangleFilledIcon
                v-show="sortKey === 'value'"
                size="14"
                :style="{ position: 'relative', top: '0.2rem', left: '0.2rem', transform: sortDir === 'descending' ? 'rotate(180deg)' : 'rotate(0deg)' }"  />
            </th>
          </tr>
          <tbody>
            <tr 
              v-for="facet in facetList" 
              :key="facet.value"
              @click="applyFilter(facet.value)">
              <td><input type="checkbox" :checked="selectedFilters.includes(facet.value)" /> </td>
              <td>{{facet.count}}</td>
              <td>{{facet.displayValue || facet.value}}</td>
            </tr>
          </tbody>
        </table>
      </OverlayScrollbars>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Ref, ref, watch } from 'vue';
import { ArrowLeftIcon, TriangleFilledIcon } from 'vue-tabler-icons';
import { fetchFacetList } from '@/services/SolrService'
import { useMimeStore } from '@/store/mime';

const emit = defineEmits(['close', 'applyFilter']);
const props = defineProps({
  fieldName: {
    type: String,
    required: false,
    default: undefined
  },
  selectedFilters: {
    type: Array,
    required: false,
    default: []
  }
});

const applyFilter = (value: string) => {
  emit('applyFilter', { fieldName: props.fieldName, value });
}

const loading = ref(true);
const facetList: Ref<any[]> = ref([]);
const { getMimeTypeDisplay } = useMimeStore();
const sortDir = ref('descending');
const sortKey = ref('count');
const doSort = (columnName: string) => {
  if(sortKey.value === columnName) {
    sortDir.value = sortDir.value === 'descending' ? 'ascending' : 'descending';
  } else {
    sortKey.value === 'count' ? sortDir.value = 'descending' : sortDir.value = 'ascending';
  }
  sortKey.value = columnName;
  if(sortKey.value === 'count') {
    facetList.value.sort((a, b) => {
      if(sortDir.value === 'descending') {
        return a.count - b.count;
      } else {
        return b.count - a.count;
      }
    });
    return;
  }
  facetList.value.sort((a, b) => {
    const valueA = a.displayValue?.toLowerCase() || a.value.toLowerCase();
    const valueB = b.displayValue?.toLowerCase() || b.value.toLowerCase();
    if(sortDir.value === 'descending') {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  });
}

const scrollbarOptions = {
    scrollbars: {
      theme: 'os-theme-light',
    },
  };



watch(() => props.fieldName, async(fieldName) => {
  if(fieldName && fieldName.length > 0) {
    loading.value = true;
    await fetchFacetList(fieldName).then((response) => {
      if(fieldName === 'format') {
        response = response.map((facet) => {
          facet.displayValue = getMimeTypeDisplay(facet.val);
          return facet;
        });
      }
      response = response.map(f => ({...f, value: f.val}))
      facetList.value = response;
    });
  }
  loading.value = false;
}, { immediate: true });
</script>

<style lang="scss" scoped>
.wrapper {
  height: 100%;
  width: 100%;
  background-color: #1e1e1ea6;
  display: flex;
  justify-content: center;
  align-items: center;
}

.facet-list {
  background-color: var(--app-background);
  width: clamp(12rem, 100%, 50rem);
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-top:1rem;
  margin-top:1rem;
  margin-bottom: 1rem;
  border-radius: var(--border-radius);
  border-style: solid;
  border-color: #ffffff38;
  overflow-y: auto;
  overflow-x: hidden;

  h3 {
    text-transform: capitalize;
  }
  .close-button {
    position: absolute;
    top: 1rem;
    left: 1rem;
    width: 2rem;
    height: 2rem;
    border-radius: 1rem;
    transition: all 0.25s ease;
    background-color: unset;
    &:hover {
      cursor: pointer;
      background-color: var(--color-background-light);
    }
  }
}

table {
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  text-align: left;
  word-break: break-all;
  border-collapse: collapse; 
  width: 100%;
  table-layout: fixed;
  th {
    text-underline-offset: 2px;
    min-width: 7ch;
    &:hover {
      cursor: pointer;
      background-color: var(--color-background-light);
    }
    padding: 0rem 1rem 0px 0px;
  }
  th:first-child {
    width: 2ch;
  }
  th:nth-child(2) {
    width: 12ch;
    text-align: right;
  }
  tr {
    border-bottom: 1px solid #cccccc76;
    td {
      padding: 0.5rem 1rem 0.5rem 0rem;
    }
   
  }

  tbody {
    tr {
      :first-child {
        padding-left: 1rem;
        width: 3ch;
      }
      :nth-child(2) {
        text-align: right;
        padding-right: 1rem;
      }
      &:hover {
        background-color: var(--color-background-light);
        cursor: pointer;
      }
      td :last-child {
        padding-right: 1rem;
      }
    }
  }
}

</style>